import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import React, { SyntheticEvent } from "react";
import { useDispatch, useSelector } from "react-redux";

import Radio from "@material-ui/core/Radio";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import { useRacehorse360Api } from "hooks/api";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { setSelectedFacilityId as setConditionBookSelectedFacilityId } from "store/actions/conditionBookPage";
import { setSelectedFacilityIds } from "store/actions/queriesConfig";
import { setSelectedFacility as setForecastSelectedFacility } from "store/actions/trainerDashboardPageForecast";
import { setSelectedFacility as setWorkOrdersSelectedFacility } from "store/actions/workOrdersPage";
import { setSelectedFacility as setWorkoutsSelectedFacility } from "store/actions/workoutsPage";
import { FacilitiesState } from "store/reducers/facilities";
import { horsesToDisplayFilterTracks, onTrackHorses } from "utils/facilities";

import useStyles from "./styles";

interface IProps {
  handleBlockableLinkClick: (e: SyntheticEvent) => boolean;
  setOpen: (state: boolean) => void;
}

export const ALL_LOCATIONS_ID = "allLocations";
export const ON_TRACK_HORSES_ID = "onTrackHorses";

const FacilitySelector = (props: IProps) => {
  const { handleBlockableLinkClick, setOpen } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { currentUser } = useLoggedInUser();

  const selectedFacilityIds = useAppSelector(
    state => state?.queriesConfig?.selectedFacilityIds
  );

  const { facilities } = useSelector(
    (state: { facilities: FacilitiesState }) =>
      state?.facilities || { facilities: [] }
  );

  const { usePullHorsesCountByFacilitySummary } = useRacehorse360Api();

  const {
    data: horsesCountDataByFacilities,
    isLoading: isLoadingHorsesCountData,
    isFetching: isFetchingHorsesCountData
  } = usePullHorsesCountByFacilitySummary(
    `${currentUser.trainerId}-horsesCountByFacility`,
    {
      facilityIds: horsesToDisplayFilterTracks
    },
    {
      enabled: currentUser.isTrainer
    }
  );

  const allLocationsItem: racehorse360.IHorsesCountByFacilitySummary = {
    facilityId: ALL_LOCATIONS_ID,
    facilityName: "All Locations",
    horsesCount: horsesCountDataByFacilities?.totalCount || 0
  };
  const onTrackHorsesItem: racehorse360.IHorsesCountByFacilitySummary = {
    facilityId: ON_TRACK_HORSES_ID,
    facilityName: "On-Track Horses",
    horsesCount:
      horsesCountDataByFacilities?.totalCountAtRequestedFacilities || 0
  };
  const facilityItems: racehorse360.IHorsesCountByFacilitySummary[] =
    horsesCountDataByFacilities?.horsesCountByFacilitySummary || [];

  let selectedFacilityId: string;

  if (selectedFacilityIds?.length > 1) {
    selectedFacilityId = onTrackHorsesItem.facilityId;
  } else if (selectedFacilityIds?.length === 1) {
    selectedFacilityId = selectedFacilityIds[0];
  } else {
    selectedFacilityId = allLocationsItem.facilityId;
  }

  const isLoading = [isLoadingHorsesCountData, isFetchingHorsesCountData].some(
    Boolean
  );

  const handleChangeFacility = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (handleBlockableLinkClick(event)) {
      setOpen(false);
    } else {
      const currenTargetValue = event.currentTarget.value;
      let facilityIds: string[];

      if (currenTargetValue === allLocationsItem.facilityId) {
        facilityIds = [];
      } else if (currenTargetValue === onTrackHorsesItem.facilityId) {
        facilityIds = onTrackHorses;
      } else {
        facilityIds = [currenTargetValue];
      }

      dispatch(setSelectedFacilityIds(facilityIds));

      if (facilityIds?.length === 1) {
        const selectedFacility = facilities.filter(
          f => f.id === facilityIds[0]
        )[0];

        dispatch(setForecastSelectedFacility(selectedFacility));
        dispatch(setWorkoutsSelectedFacility(selectedFacility));
        dispatch(setWorkOrdersSelectedFacility(selectedFacility));
        dispatch(setConditionBookSelectedFacilityId(selectedFacility.id));
      }

      await queryClient.invalidateQueries();
    }
  };

  const renderFacilitiesList = () => {
    if (isLoading) {
      return <Loader className={classes.loader} />;
    }

    const preparedData = [
      onTrackHorsesItem,
      allLocationsItem,
      ...facilityItems
    ];

    return preparedData.map(facility => {
      const { facilityId, facilityName, horsesCount } = facility;

      const isCustomOption = [
        allLocationsItem.facilityId,
        onTrackHorsesItem.facilityId
      ].some(itemId => itemId === facilityId);

      const optionName = isCustomOption
        ? facilityName
        : facilityName.toLowerCase();

      return (
        <label key={facilityId} className={classes.listItem}>
          <span>{`${optionName} (${horsesCount || 0})`}</span>
          <Radio
            classes={{
              root: classes.radioButton
            }}
            checked={selectedFacilityId === facilityId}
            color="default"
            value={facilityId}
            onChange={handleChangeFacility}
            size="small"
          />
        </label>
      );
    });
  };

  return (
    <div className={classes.facilitySelector}>
      <div className={clsx(classes.listItem, classes.header)}>
        Horses to Display
      </div>
      {renderFacilitiesList()}
    </div>
  );
};

export default React.memo(FacilitySelector);
